"use client";

import React from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import { cn } from "@/lib/utils";
import { IVideoPopup } from "@/sanity/lib/definitions";
import { Dialog, DialogClose, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger } from "@/shadcn/ui/dialog";
import { CTACornerButton } from "@/shadcn/ui/corner-button";

import closeIcon from "@/public/icons/Close--large.svg";

const VideoPopupPlayer = dynamic(() => import("@/components/global/partials/VideoPopupPlayer"), {
  ssr: false,
  loading: () => <div className="aspect-video w-full bg-black" />,
});

export default function VideoPopup({
  data,
  title,
  className,
  children,
}: {
  data: IVideoPopup;
  title?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <div className={cn("w-full md:w-auto", className)}>{children}</div>
      </DialogTrigger>

      <DialogPortal>
        <DialogOverlay className="bg-black/70 backdrop-blur-[1.25rem]" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="fixed inset-x-0 top-[8.25rem] z-[999] mx-auto flex w-[calc(100%-6rem)] flex-col outline-0 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 md:inset-y-0 md:my-auto md:h-fit md:w-[min(142.375rem,calc(100vw-4rem),calc((100svh-22.125rem)*16/9+3.25rem))]"
        >
          <VisuallyHidden>
            <DialogTitle>{title || "Video"}</DialogTitle>
          </VisuallyHidden>

          <DialogClose className="flex items-center gap-[1rem] self-end bg-white py-[1rem] pl-[1rem] pr-[1rem] font-geistmono text-[1.75rem] uppercase leading-none text-[#202223] md:pl-[2rem] md:pr-[2rem] md:text-[2.25rem]">
            <Image
              src={closeIcon}
              alt=""
              className="h-[2.5rem] w-[2.5rem] md:h-[3.5rem] md:w-[3.5rem]"
            />
            Close
          </DialogClose>

          <div className="flex flex-col items-center gap-[2.25rem] bg-white p-[2.25rem] [clip-path:polygon(0_0,100%_0,100%_calc(100%-1.9375rem),calc(100%-1.9375rem)_100%,0_100%)] md:gap-[2rem] md:p-[1.625rem] md:pb-[2rem] md:[clip-path:polygon(0_0,100%_0,100%_calc(100%-3.825rem),calc(100%-3.825rem)_100%,0_100%)]">
            <VideoPopupPlayer video={data?.video} title={title} />

            {data?.cta?.cta && <CTACornerButton data={data.cta} className="w-auto" />}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
